import { metrics, type Counter, type Histogram } from '@opentelemetry/api';

export class ClientMetrics {
  // --- Failure metrics (section 1) ---
  readonly wsConnectionFailures: Counter;
  readonly wsMessageParseErrors: Counter;
  readonly wsReconnectionExhausted: Counter;
  readonly gameAutoSubmitFailures: Counter;
  readonly gameHeldSubmissionDropped: Counter;
  readonly gameTimerDesyncDetected: Counter;
  readonly gameStateMismatch: Counter;
  readonly gameActionRejected: Counter;
  readonly uiConnectionGiveUp: Counter;
  readonly clientCodeErrors: Counter;

  // --- WebSocket metrics (section 3) ---
  readonly wsReconnections: Counter;
  readonly wsMessagesReceived: Counter;
  readonly wsMessagesSent: Counter;
  readonly wsConnectionDurationMs: Histogram;

  // --- User interaction metrics (section 3) ---
  readonly uiLetterSelectionDurationMs: Histogram;
  readonly uiWordSubmissionDurationMs: Histogram;

  // --- Page performance (section 3) ---
  readonly pageLcpMs: Histogram;
  readonly pageInpMs: Histogram;
  readonly pageCls: Histogram;
  readonly pageTtfbMs: Histogram;

  // --- WebSocket lag ---
  readonly wsRequestRttMs: Histogram;
  readonly wsPingRttMs: Histogram;
  readonly wsOpenToFirstMessageMs: Histogram;

  // --- Time-to-playable ---
  readonly gameTimeToPlayableMs: Histogram;

  // --- Reconnection experience ---
  readonly wsReconnectDurationMs: Histogram;

  // --- Outbound traffic visibility ---
  readonly wsMessageBytesSent: Histogram;
  readonly wsMessageBytesReceived: Histogram;

  // --- SPA-level UX ---
  readonly spaLongtasks: Counter;
  readonly spaLongtaskDurationMs: Histogram;
  readonly spaRouteChangeDurationMs: Histogram;
  readonly spaResourceLoadFailures: Counter;
  readonly spaTabBackgrounded: Counter;

  constructor() {
    const meter = metrics.getMeter('lexicontest-client');

    // Failure metrics
    this.wsConnectionFailures = meter.createCounter('ws_connection_failures', {
      description: 'WebSocket connection failures',
    });
    this.wsMessageParseErrors = meter.createCounter('ws_message_parse_errors', {
      description: 'WebSocket message JSON parse errors',
    });
    this.wsReconnectionExhausted = meter.createCounter('ws_reconnection_exhausted', {
      description: 'WebSocket reconnection attempts exhausted',
    });
    this.gameAutoSubmitFailures = meter.createCounter('game_auto_submit_failures', {
      description: 'Auto-submission failures when timer expires',
    });
    this.gameHeldSubmissionDropped = meter.createCounter('game_held_submission_dropped', {
      description: 'Held word submissions dropped without being sent, by reason',
    });
    this.gameTimerDesyncDetected = meter.createCounter('game_timer_desync_detected', {
      description: 'Timer desynchronization events exceeding 1s',
    });
    this.gameStateMismatch = meter.createCounter('game_state_mismatch', {
      description: 'Unknown game event types received',
    });
    this.gameActionRejected = meter.createCounter('game_action_rejected', {
      description: 'Server-rejected game actions',
    });
    this.uiConnectionGiveUp = meter.createCounter('ui_connection_give_up', {
      description: 'Games ended because the app gave up on the connection, by reason',
    });
    this.clientCodeErrors = meter.createCounter('client_code_errors', {
      description: 'Client-side errors by location',
    });

    // WebSocket metrics
    this.wsReconnections = meter.createCounter('ws_reconnections', {
      description: 'WebSocket reconnection attempts',
    });
    this.wsMessagesReceived = meter.createCounter('ws_messages_received', {
      description: 'WebSocket messages received',
    });
    this.wsMessagesSent = meter.createCounter('ws_messages_sent', {
      description: 'WebSocket messages sent',
    });
    this.wsConnectionDurationMs = meter.createHistogram('ws_connection_duration_ms', {
      description: 'WebSocket connection duration',
      unit: 'ms',
    });

    // User interaction metrics
    this.uiLetterSelectionDurationMs = meter.createHistogram('ui_letter_selection_duration_ms', {
      description: 'Time spent in letter selection phase',
      unit: 'ms',
    });
    this.uiWordSubmissionDurationMs = meter.createHistogram('ui_word_submission_duration_ms', {
      description: 'Time from in-progress start to word submission',
      unit: 'ms',
    });

    // Page performance
    this.pageLcpMs = meter.createHistogram('page_lcp_ms', {
      description: 'Largest Contentful Paint',
      unit: 'ms',
    });
    this.pageInpMs = meter.createHistogram('page_inp_ms', {
      description: 'Interaction to Next Paint',
      unit: 'ms',
    });
    this.pageCls = meter.createHistogram('page_cls', {
      description: 'Cumulative Layout Shift',
    });
    this.pageTtfbMs = meter.createHistogram('page_ttfb_ms', {
      description: 'Time to First Byte',
      unit: 'ms',
    });

    // WebSocket lag
    this.wsRequestRttMs = meter.createHistogram('ws_request_rtt_ms', {
      description: 'WebSocket request-to-response round-trip time, by request type',
      unit: 'ms',
    });
    this.wsPingRttMs = meter.createHistogram('ws_ping_rtt_ms', {
      description: 'WebSocket Ping/Pong round-trip time',
      unit: 'ms',
    });
    this.wsOpenToFirstMessageMs = meter.createHistogram('ws_open_to_first_message_ms', {
      description: 'Time from WebSocket open to first server message',
      unit: 'ms',
    });

    // Time-to-playable
    this.gameTimeToPlayableMs = meter.createHistogram('game_time_to_playable_ms', {
      description: 'Time from match-request send to first playable state',
      unit: 'ms',
    });

    // Reconnection experience
    this.wsReconnectDurationMs = meter.createHistogram('ws_reconnect_duration_ms', {
      description: 'WebSocket reconnect duration by outcome',
      unit: 'ms',
    });

    // Outbound traffic visibility
    this.wsMessageBytesSent = meter.createHistogram('ws_message_bytes_sent', {
      description: 'WebSocket sent payload size in bytes',
      unit: 'By',
    });
    this.wsMessageBytesReceived = meter.createHistogram('ws_message_bytes_received', {
      description: 'WebSocket received payload size in bytes',
      unit: 'By',
    });

    // SPA-level UX
    this.spaLongtasks = meter.createCounter('spa_longtasks', {
      description: 'PerformanceObserver longtask entries observed',
    });
    this.spaLongtaskDurationMs = meter.createHistogram('spa_longtask_duration_ms', {
      description: 'Duration of individual longtask entries',
      unit: 'ms',
    });
    this.spaRouteChangeDurationMs = meter.createHistogram('spa_route_change_duration_ms', {
      description: 'Vue Router navigation duration',
      unit: 'ms',
    });
    this.spaResourceLoadFailures = meter.createCounter('spa_resource_load_failures', {
      description: 'Resource (img, script, link, media) load failures',
    });
    this.spaTabBackgrounded = meter.createCounter('spa_tab_backgrounded', {
      description: 'Tab visibility transitions to hidden',
    });
  }
}

let instance: ClientMetrics | null = null;

export function getMetrics(): ClientMetrics {
  if (!instance) {
    instance = new ClientMetrics();
  }
  return instance;
}
